import { BaseHttpClient } from "../../http/base-http-client";
import type {
  ChatSession,
  ChatSessionWithMessages,
  Message,
  PostMessageResponse,
  SessionsDeletedResponse,
  MessageFeedbackResponse,
  CreateChatSessionRequest,
  ListChatSessionsParams,
  ChatSessionUpdateRequestBody,
  UserMessageCreate,
  PostMessageParams,
  MessageFeedbackRequestBody,
} from "../../types/api/chat-types";
import type { PaginatedResponse } from "../../types/api/common-types";
import { collectLightweightUserMetadata } from "../../utils/user-metadata";

/**
 * Chat API Client
 *
 * Handles all chat-related operations including:
 * - Chat session management (CRUD operations)
 * - Message operations (send, retry, edit, delete, versioning)
 * - Branch operations (set active branch)
 *
 * This client follows the Single Responsibility Principle by focusing
 * solely on chat domain operations.
 */
export class ChatApiClient extends BaseHttpClient {
  // ==================== CHAT SESSION OPERATIONS ====================

  /**
   * Create a new chat session for an agent
   * @param agentId - The agent ID to create session for
   * @param data - Optional session creation data
   * @returns Promise<ChatSessionWithMessagesAndBranches>
   */
  async createChatSession(
    agentId: string,
    data?: CreateChatSessionRequest
  ): Promise<ChatSessionWithMessages> {
    return this.post<ChatSessionWithMessages>(
      `/agents/${agentId}/sessions`,
      data
    );
  }

  /**
   * List chat sessions for an agent with pagination and search
   * @param agentId - The agent ID to list sessions for
   * @param params - Optional pagination and search parameters
   * @returns Promise<PaginatedResponse<ChatSession>>
   */
  async listChatSessions(
    agentId: string,
    params?: ListChatSessionsParams
  ): Promise<PaginatedResponse<ChatSession>> {
    // Only send skip, limit, and search parameters that the backend expects
    const queryParams: Partial<ListChatSessionsParams> = {};
    if (params?.skip !== undefined) queryParams.skip = params.skip;
    if (params?.limit !== undefined) queryParams.limit = params.limit;
    if (params?.search !== undefined) queryParams.search = params.search;

    return this.get<PaginatedResponse<ChatSession>>(
      `/agents/${agentId}/sessions`,
      queryParams
    );
  }

  /**
   * Get a specific chat session with its messages and validation
   * @param agentId - The agent ID
   * @param sessionId - The session ID to retrieve
   * @returns Promise<ChatSessionWithMessages>
   */
  async getChatSession(
    agentId: string,
    sessionId: string
  ): Promise<ChatSessionWithMessages> {
    const response = await this.get<ChatSessionWithMessages>(
      `/agents/${agentId}/sessions/${sessionId}`
    );

    // Add validation logic from original api-client.ts
    if (!response || typeof response !== "object") {
      console.error(
        `API returned invalid response for session ${sessionId}:`,
        response
      );
      throw new Error(`Invalid response for session ${sessionId}`);
    }

    if (!response.id) {
      console.error(
        `CRITICAL: API for session ${sessionId} did not return an 'id'. Backend MUST be fixed. Response:`,
        response
      );
      throw new Error(
        `Backend did not return session ID for session ${sessionId}`
      );
    }

    return response;
  }

  /**
   * Update a chat session (e.g., rename it)
   * @param agentId - The agent ID
   * @param sessionId - The session ID to update
   * @param data - Update data containing new session properties
   * @returns Promise<ChatSession>
   */
  async updateChatSession(
    agentId: string,
    sessionId: string,
    data: ChatSessionUpdateRequestBody
  ): Promise<ChatSession> {
    return this.put<ChatSession>(
      `/agents/${agentId}/sessions/${sessionId}`,
      data
    );
  }

  /**
   * Delete a specific chat session
   * @param agentId - The agent ID
   * @param sessionId - The session ID to delete
   * @returns Promise<void>
   */
  async deleteChatSession(agentId: string, sessionId: string): Promise<void> {
    return this.delete(`agents/${agentId}/sessions/${sessionId}`);
  }

  /**
   * Delete all chat sessions for an agent
   * @param agentId - The agent ID to delete all sessions for
   * @returns Promise<SessionsDeletedResponse>
   */
  async deleteAllChatSessions(
    agentId: string
  ): Promise<SessionsDeletedResponse> {
    return this.delete<SessionsDeletedResponse>(
      `/agents/${agentId}/bulk-operations/delete-all-sessions`
    );
  }

  // ==================== MESSAGE OPERATIONS ====================

  /**
   * Send a message to a chat session
   * @param agentId - The agent ID
   * @param sessionId - The session ID to send message to
   * @param data - Message content and metadata
   * @param params - Optional query parameters (model, language, etc.)
   * @returns Promise<PostMessageResponse>
   */
  async postMessage(
    agentId: string,
    sessionId: string,
    data: UserMessageCreate,
    params?: PostMessageParams
  ): Promise<PostMessageResponse> {
    // Validate session ID (preserve original validation logic)
    if (!sessionId || sessionId === "undefined") {
      console.error(
        "[API Client] Invalid session ID provided to postMessage:",
        sessionId
      );
      throw new Error("Invalid session ID. Cannot send message.");
    }

    // Collect user metadata if not already provided
    if (!data.user_metadata) {
      try {
        console.log("[API Client] Collecting user metadata...");
        const metadata = await collectLightweightUserMetadata();
        console.log("[API Client] Collected metadata:", metadata);
        // Convert to plain object for serialization
        data.user_metadata = {
          timezone: metadata.timezone,
          timezoneOffset: metadata.timezoneOffset,
          locale: metadata.locale,
          language: metadata.language,
          languages: metadata.languages,
          device: metadata.device,
          browser: metadata.browser,
          screen: metadata.screen,
          network: metadata.network,
          session: metadata.session,
          ...(metadata.location && { location: metadata.location }),
        };
        console.log(
          "[API Client] Set user_metadata on data:",
          data.user_metadata
        );
      } catch (error) {
        console.error("[API Client] Failed to collect user metadata:", error);
        // Continue without metadata - non-blocking
      }
    } else {
      console.log(
        "[API Client] user_metadata already provided:",
        data.user_metadata
      );
    }

    // Auto-include current locale if not explicitly provided
    const finalParams = {
      ...params,
      language: params?.language || this.getCurrentLocale(),
    };

    const urlParams = new URLSearchParams();
    Object.entries(finalParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        urlParams.append(key, String(value));
      }
    });

    const url = `/agents/${agentId}/sessions/${sessionId}/messages?${urlParams}`;
    return this.post<PostMessageResponse>(url, data);
  }

  /**
   * Edit a user message and get a new response
   * @param agentId - The agent ID
   * @param sessionId - The session ID
   * @param messageId - The message ID to edit
   * @param data - New message content and metadata
   * @param params - Optional query parameters
   * @returns Promise<UserMessageVersionResponse>
   */
  async editUserMessageAndGetResponse(
    agentId: string,
    sessionId: string,
    messageId: string,
    data: UserMessageCreate,
    params?: PostMessageParams
  ): Promise<PostMessageResponse> {
    // Validate session ID
    if (!sessionId || sessionId === "undefined") {
      console.error(
        "[API Client] Invalid session ID provided to editUserMessageAndGetResponse:",
        sessionId
      );
      throw new Error("Invalid session ID. Cannot edit message.");
    }

    // Auto-include current locale if not explicitly provided
    const finalParams = {
      ...params,
      language: params?.language || this.getCurrentLocale(),
    };

    const urlParams = new URLSearchParams();
    Object.entries(finalParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        urlParams.append(key, String(value));
      }
    });

    const url = `/agents/${agentId}/sessions/${sessionId}/messages/${messageId}/edit?${urlParams}`;
    return this.post<PostMessageResponse>(url, data);
  }

  /**
   * Delete a specific message
   * @param agentId - The agent ID
   * @param sessionId - The session ID
   * @param messageId - The message ID to delete
   * @returns Promise<Message>
   */
  async deleteMessage(
    agentId: string,
    sessionId: string,
    messageId: string
  ): Promise<Message> {
    // Validate session ID
    if (!sessionId || sessionId === "undefined") {
      console.error(
        "[API Client] Invalid session ID provided to deleteMessage:",
        sessionId
      );
      throw new Error("Invalid session ID. Cannot delete message.");
    }

    return this.delete<Message>(
      `/agents/${agentId}/sessions/${sessionId}/messages/${messageId}`
    );
  }

  /**
   * Get a specific version of a message
   * @param agentId - The agent ID
   * @param sessionId - The session ID
   * @param messageId - The message ID
   * @param version - The version number to retrieve
   * @returns Promise<Message>
   */
  async getMessageVersion(
    agentId: string,
    sessionId: string,
    messageId: string,
    version: number
  ): Promise<Message> {
    // Validate session ID
    if (!sessionId || sessionId === "undefined") {
      console.error(
        "[API Client] Invalid session ID provided to getMessageVersion:",
        sessionId
      );
      throw new Error("Invalid session ID. Cannot get message version.");
    }

    return this.get<Message>(
      `/agents/${agentId}/sessions/${sessionId}/messages/${messageId}/versions/${version}`
    );
  }

  /**
   * Revert to a specific version of a message
   * @param agentId - The agent ID
   * @param sessionId - The session ID
   * @param messageId - The message ID
   * @param version - The version number to revert to
   * @returns Promise<Message>
   */
  async revertToMessageVersion(
    agentId: string,
    sessionId: string,
    messageId: string,
    version: number
  ): Promise<Message> {
    // Validate session ID
    if (!sessionId || sessionId === "undefined") {
      console.error(
        "[API Client] Invalid session ID provided to revertToMessageVersion:",
        sessionId
      );
      throw new Error("Invalid session ID. Cannot revert message version.");
    }

    return this.post<Message>(
      `/agents/${agentId}/sessions/${sessionId}/messages/${messageId}/versions/${version}/revert`
    );
  }

  /**
   * Submit feedback for a message
   * @param messageId - The message ID to provide feedback for
   * @param data - Feedback content and type
   * @returns Promise<MessageFeedbackResponse>
   */
  async submitMessageFeedback(
    messageId: string,
    data: MessageFeedbackRequestBody
  ): Promise<MessageFeedbackResponse> {
    return this.post<MessageFeedbackResponse>(
      `/messages/${messageId}/feedback`,
      data
    );
  }

  // ==================== BRANCH OPERATIONS ====================

  /**
   * Set the active branch for a session
   * @param sessionId - The session ID
   * @param branchId - The branch ID to set as active
   * @param messageId - Optional message ID for specific branching point
   * @param versionIndex - Optional version index
   * @returns Promise<void>
   */
  async setActiveBranch(
    sessionId: string,
    branchId: string,
    messageId?: string,
    versionIndex?: number
  ): Promise<void> {
    // Clean messageId logic from original api-client.ts
    const cleanMessageId = messageId?.startsWith("confirmed-")
      ? messageId.substring("confirmed-".length)
      : messageId;

    const params: PostMessageParams = {};
    if (cleanMessageId && versionIndex !== undefined) {
      params.message_id = cleanMessageId;
      params.version_index = versionIndex;
    }

    return this.post<void>(
      `/sessions/${sessionId}/set-active-branch/${branchId}`,
      {},
      { params }
    );
  }

  // ==================== HELPER METHODS ====================

  /**
   * Get current locale for API requests
   * @private
   */
  private getCurrentLocale(): string {
    // This logic is preserved from the original api-client.ts
    if (typeof window !== "undefined") {
      return document.documentElement.lang || "en";
    }
    return "en";
  }
}
